import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Placeholder from 'react-bootstrap/Placeholder';
import i18next from '../../translations/i18n';
import API from '../../services/API';
import heap from '../../containers/App/heap';
import AppTypeContext from '../../contexts/AppTypeContext';
import EmptyResult from '../EmptyResult';

const PlaceholderDiv = () => (
  <>
    <Placeholder as={Card.Title} animation="glow">
      <Placeholder xs={4} size="lg" />
    </Placeholder>
    <Row className="g-5 py-5">
      {[...Array(3).keys()].map((item) => (
        <Col xs={12} sm={6} md={6} lg={4} key={item}>
          <Card className="" key={item}>
            <Card.Body className="p-4 shadow d-flex flex-column">
              <Placeholder as={Card.Text} animation="glow">
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
              </Placeholder>
              <Placeholder.Button
                xs={4}
                variant="primary"
                aria-hidden="true"
                className="wt-link-btn"
              >
                &nbsp;
              </Placeholder.Button>
            </Card.Body>
          </Card>
        </Col>
      ))}
      {[...Array(2).keys()].map((item) => (
        <Col xs={12} sm={6} md={6} lg={4} key={item}>
          <Card className="" key={item}>
            <Card.Body className="p-4 shadow d-flex flex-column">
              <Placeholder as={Card.Text} animation="glow">
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
                <Placeholder xs={12} size="lg">
                  &nbsp;
                </Placeholder>
              </Placeholder>
              <Placeholder.Button
                xs={4}
                variant="primary"
                aria-hidden="true"
                className="wt-link-btn"
              >
                &nbsp;
              </Placeholder.Button>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  </>
);

const ProductsTable = (props) => {
  const appName = useContext(AppTypeContext);
  const [loading, setLoading] = useState(true);
  const [longProducts, setLongProducts] = useState([]);
  const [shortProducts, setShortProducts] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const { underlying, assetClass, postTitle } = props;

  const getProducts = (products, title) => (
    <>
      <Card.Title as="h4">{title}</Card.Title>
      <Row className="g-5 py-5">
        {products.map((product) => (
          <Col xs={12} sm={6} md={6} lg={4} key={product.name}>
            <Card className="h-100">
              <Card.Body className="p-4 shadow d-flex flex-column">
                <Card.Text className="mb-1 fw-bold">{product.name}</Card.Text>
                <Card.Text className="mb-3 text-muted">
                  {i18next.t('Base Currency')}:{' '}
                  <strong className="fw-bold">{product.baseCurrency}</strong>
                </Card.Text>
                <Button
                  type={postTitle ? `${appName}-${postTitle}` : appName}
                  name={product.name}
                  variant="primary"
                  className="text-uppercase fw-bold wt-product-link mt-auto wt-link-btn pe-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    API.sendToGA(
                      postTitle
                        ? `${appName}-product-${product.name}-${postTitle}`
                        : `${appName}-product-${product.name}`,
                    );
                    heap.track('ClickProducts', { name: product.name });
                    window.open(product.productPageURL, '_blank');
                  }}
                >
                  View Product
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );

  const loadProducts = async () => {
    const response = await API.getProductsFromUnderlying(underlying);
    // filter long products only - we have to use both names and leverage factor as fallback until leverage factor value are fixed across all products
    const lp = await response.filter((x) => {
      if (assetClass === 'Currencies') {
        return true;
      }
      const lIndex = x.name.toLowerCase().indexOf('long');
      const sIndex = x.name.toLowerCase().indexOf('short');
      if (lIndex < 0 && sIndex < 0) {
        // boost long products do not include "long"
        return x.leverageFactor.indexOf('-') < 0;
      }
      return lIndex >= 0 && (sIndex < 0 || lIndex < sIndex);
    });
    // filter short products only
    const sp = await response.filter((x) => {
      const lIndex = x.name.toLowerCase().indexOf('long');
      const sIndex = x.name.toLowerCase().indexOf('short');
      return sIndex >= 0 && (lIndex < 0 || sIndex < lIndex);
    });
    await setLongProducts(lp);
    await setShortProducts(sp);
    await setProductsList(lp.concat(sp));
    await setLoading(false);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  if (loading) {
    return <PlaceholderDiv />;
  }

  if (!loading && longProducts.length <= 0 && shortProducts.length <= 0) {
    return <EmptyResult message={i18next.t('ProdNotAvailable')} />;
  }

  // currencies should be displayed in single table
  if (assetClass === 'Currencies') {
    return getProducts(
      longProducts,
      `${i18next.t('ProductRelatedTo')} ${i18next.t(underlying)}`,
    );
  }

  return (
    productsList.length > 0 &&
    getProducts(
      productsList,
      `${i18next.t('ProductRelatedTo')} ${i18next.t(underlying)}`,
    )
  );
};

ProductsTable.defaultProps = {
  postTitle: '',
};
ProductsTable.propTypes = {
  underlying: PropTypes.string.isRequired,
  assetClass: PropTypes.string.isRequired,
  postTitle: PropTypes.string,
};

export default ProductsTable;
